import * as amplitude from '@amplitude/analytics-browser';
import {
  FinanceApplication,
  FinanceResponse,
} from '@upstart/harmony/lib/legos/financingTypes';
import { FinanceProvider, WebStep } from '@upstart/harmony/lib/legos/constants';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { isNumber, isString } from 'lodash';

export interface UserProperties {
  dealerId?: string;
  userId?: number;
  userType?: string;
  userPermission?: string[];
  userRole?: string | null;
  experiments?: string;
}

export type DeskingEventProperties = {
  dealId?: string;
  financeResponseId?: string;
  financeApplicationId?: string;
  financeResponseStatus?: string;
  upstartRateId?: string;
  provider?: string;
  providerApplicationId?: string;
  upstartOfferBadge?: string;
  apr?: string;
  term?: string;
  monthlyPayment?: string;
  deskingTab?: string;
  fniPresentFlow?: boolean;
};

export type EventProperties = {
  carMake?: string | null;
  carYear?: number | null;
  carModel?: string | null;
  vin?: string | null;
  shareDealMedium?: Array<'email' | 'phone'>;
  shareDealStepsInclude?: Array<WebStep>;
  shareDealStepStart?: WebStep;
} & DeskingEventProperties;

export const init = () => {
  if (
    typeof window === 'undefined' ||
    !process.env.REACT_APP_AMPLITUDE_API_KEY
  ) {
    return;
  }
  const domain = process.env.REACT_APP_BACKEND_URL
    ? new URL(process.env.REACT_APP_BACKEND_URL).origin
    : '';

  amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, undefined, {
    serverUrl: `${domain}/analytics/amplitudeV2`,
    defaultTracking: true,
    minIdLength: 1,
  });
  const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });
  amplitude.add(sessionReplayTracking);
};

export const setUserProperties = (properties: UserProperties) => {
  if (!process.env.REACT_APP_AMPLITUDE_API_KEY) {
    return;
  }
  const identifyEvent = new amplitude.Identify();
  Object.entries(properties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  amplitude.identify(identifyEvent);
};

export const setUserId = (userId: number) => {
  if (!process.env.REACT_APP_AMPLITUDE_API_KEY) {
    return;
  }
  amplitude.setUserId(`${userId}`);
};

export const clearUserProperties = () => {
  if (!process.env.REACT_APP_AMPLITUDE_API_KEY) {
    return;
  }
  const identifyEvent = new amplitude.Identify();
  identifyEvent.clearAll();
  amplitude.identify(identifyEvent);
  amplitude.reset();
};

export const sendData = (
  eventType: string,
  eventProperties?: EventProperties,
) => {
  try {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('Amplitude', eventType, eventProperties);
    }
    if (!process.env.REACT_APP_AMPLITUDE_API_KEY) {
      return;
    }
    amplitude.track(eventType, eventProperties);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Amplitude error', e);
  }
};

export const safeParseStringEventProperties = (
  prop?: number | string | boolean | null,
): string | undefined => {
  if (isNumber(prop)) {
    return prop.toString();
  }
  if (isString(prop)) {
    return prop;
  }
  return undefined;
};

export function safeParseStringEventObject(obj: {
  [key in keyof EventProperties]: number | string | boolean | null | undefined;
}): EventProperties {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      safeParseStringEventProperties(value),
    ]),
  );
}

export const getUpstartOfferEventProperties = ({
  dealId,
  application,
  response,
  aprOverride,
}: {
  dealId?: string;
  application?: FinanceApplication | null;
  response?: FinanceResponse | null;
  aprOverride?: number | null;
}) => {
  let upstartOfferProps: DeskingEventProperties = {};
  if (application?.provider === FinanceProvider.UPSTART) {
    upstartOfferProps = {
      upstartRateId: response?.currentRate?.rateId,
      upstartOfferBadge: response?.data?.primaryValueBadge?.value ?? undefined,
    };
  }

  return safeParseStringEventObject({
    dealId,
    financeResponseStatus: response?.status,
    financeApplicationId: application?.id,
    financeResponseId: response?.id,
    providerApplicationId: application?.providerApplicationId,
    apr: aprOverride ?? response?.data?.aprOverride ?? response?.data?.apr,
    term: response?.data?.months,
    monthlyPayment: response?.data?.monthlyOverride ?? response?.data?.monthly,
    provider: application?.provider,
    ...upstartOfferProps,
  });
};
